import * as React from 'react';

import Form from '../../components/controls/forms/Form';
import { Login as FormLogin } from '../../models/forms/Login';

import { Card } from '@blueprintjs/core';

import Dialog from '../../components/common/Dialog';

import './LoginPage.scss';

import { FormErrors } from 'src/util/FormErrors';

import FormTextbox from '../controls/forms/redesign/FormTextbox';
import FormCheckbox from '../controls/forms/redesign/FormCheckbox';
import FormRow from '../controls/forms/FormRow';
import Button from '../controls/forms/redesign/FormButton';
import DialogList from 'src/dialogs';
import { AnyObject } from 'final-form';
import selectors from 'src/selectors';
import { replace } from 'connected-react-router';
import actions from 'src/actions';
import { connect } from 'react-redux';
import { IRootState } from 'src/reducers';

interface ILoginPageStateProps {
    errorMessage: string;
    isLoading: boolean;
    isLoggedIn: boolean;
    forgotPasswordDialogShowing: boolean;
    twoFactorDialogShowing: boolean;
    resetPasswordDialogShowing: boolean;
}

export interface ILoginPageDispatchProps {
    navigate: (path: string) => void;
    showForgotPassword: () => void;
    hideDialog: () => void;
    forgotPassword: (email: string) => void;
    confirmTwoFactorCode: (payload: { twoFactorAuthPin: string; persistSession: boolean }) => void;
    login: (login: FormLogin) => void;
    cancelLogin: () => void;
    resetExpiredPassword: (password: string) => void;
}

type ILoginPageProps = ILoginPageStateProps & ILoginPageDispatchProps;

class LoginPage extends React.Component<ILoginPageProps> {
    private persistSession = false;

    componentWillReceiveProps(nextProps: ILoginPageStateProps) {
        if (nextProps.isLoggedIn) {
            this.props.navigate('/');
        }
    }

    forgotPassword = () => {
        const email = (document.getElementById('email') as HTMLInputElement).value;
        this.props.forgotPassword(email);
        this.props.hideDialog();
    };

    handleSubmit = (login: FormLogin) => {
        this.persistSession = login.persistSession;
        this.props.login(login);
    };

    handleTwoFactorSubmit = (values: { twoFactorAuthPin: string }) => {
        this.props.confirmTwoFactorCode({ twoFactorAuthPin: values.twoFactorAuthPin, persistSession: this.persistSession });
    };

    resetExpiredPasswordSubmit = (values: { newPassword: string }) => {
        this.props.resetExpiredPassword(values.newPassword);
    };

    validate = (values: FormLogin) => {
        const errors: FormErrors<FormLogin> = {};

        if (values.username === undefined) {
            errors.username = 'Email address required';
        }

        if (values.password === undefined) {
            errors.password = 'Password required';
        }

        return errors as FormErrors<AnyObject>;
    };

    validateResetExpiredPassword = (values: { newPassword: string; confirmPassword: string }) => {
        const errors: FormErrors<{ newPassword: string; confirmPassword: string }> = {};

        if (values.newPassword === undefined || values.newPassword === '') {
            errors.newPassword = 'Client name required';
        }

        if (values.confirmPassword === undefined || values.confirmPassword === '') {
            errors.confirmPassword = 'Client name required';
        }

        if (values.newPassword !== values.confirmPassword) {
            errors.confirmPassword = 'Passwords must match';
        }

        return errors;
    };

    render() {
        return (
            <>
                <Card elevation={3} className="logo-panel">
                    <img src="/images/logo/logo.svg" />
                </Card>

                <Card elevation={3} className="login-panel">
                    <Form error={this.props.errorMessage} validate={this.validate} onSubmit={this.handleSubmit}>
                        <FormTextbox label="Email" name="username" isRequired={true} submitOnEnter={true} />
                        <FormTextbox
                            className="mb-1"
                            label="Password"
                            isPassword={true}
                            isRequired={true}
                            name="password"
                            autocomplete="current-password"
                            submitOnEnter={true}
                        />

                        <FormRow>
                            <div style={{ margin: '1rem 0 1rem 1rem' }}>
                                <FormCheckbox label="Keep me signed in" name="persistSession" />
                            </div>
                            <Button text="Forgot Password" buttonStyle="link" size="small" onClick={this.props.showForgotPassword} />
                        </FormRow>
                        <Button type="submit" text={this.props.isLoading ? 'Logging In...' : 'Login'} intent="primary" />
                    </Form>
                </Card>

                <Dialog title="Password Reset" dialogName={DialogList.FORGOT_PASSWORD}>
                    <Form onSubmit={this.forgotPassword}>
                        <FormTextbox label="Email" name="email" />
                        <Button type="submit" text="Reset Password" />
                    </Form>
                </Dialog>

                <Dialog title="Two Factor Authorization" dialogName={DialogList.LOGIN_SUBMIT_AUTH_CODE}>
                    <Form onSubmit={this.handleTwoFactorSubmit}>
                        <FormTextbox name="twoFactorAuthPin" label="Auth Code" />
                        <Button type="submit" text="Submit Code" />
                    </Form>
                </Dialog>

                <Dialog title="Update Password" dialogName={DialogList.UPDATE_PASSWORD} onCloseDialog={this.props.cancelLogin}>
                    <Form onSubmit={this.resetExpiredPasswordSubmit}>
                        <FormTextbox name="newPassword" label="New Password" isPassword={true} />
                        <FormTextbox name="confirmPassword" label="Confirm Password" isPassword={true} />
                        <Button type="submit" text="Update Password" />
                    </Form>
                </Dialog>
            </>
        );
    }
}

const mapStateToProps = (state: IRootState) => ({
    errorMessage: selectors.auth.error(state),
    isLoading: selectors.auth.isLoggingIn(state),
    isLoggedIn: selectors.auth.isLoggedIn(state),
    forgotPasswordDialogShowing: selectors.layout.isDialogShowing(state, 'forgotPassword'),
    twoFactorDialogShowing: selectors.layout.isDialogShowing(state, 'loginTwoFactor'),
    resetPasswordDialogShowing: selectors.layout.isDialogShowing(state, 'resetExpiredPassword'),
});

const mapDispatchToProps: ILoginPageDispatchProps = {
    navigate: (path: string) => replace(path),
    showForgotPassword: () => actions.layout.showDialog(DialogList.FORGOT_PASSWORD),
    hideDialog: () => actions.layout.hideDialog(),
    forgotPassword: (email: string) => actions.auth.forgotPassword(email),
    confirmTwoFactorCode: (payload: { twoFactorAuthPin: string; persistSession: boolean }) => actions.auth.loginTwoFactor(payload),
    login: (login: FormLogin) => actions.auth.login(login),
    cancelLogin: () => actions.auth.cancelLogin(),
    resetExpiredPassword: (password: string) => actions.auth.resetExpiredPassword(password),
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);



// WEBPACK FOOTER //
// ./src/components/login/LoginPage.tsx